import moment from "moment";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { useSelector } from "react-redux";
import { getElasticSearchURLS } from "@selectors/enviromentSelectors";
import { getAllResponsiblesState } from "@selectors/responsiblesSelectors";
import PersesSearchAPI from "@services/persesSearchService";
import {
  DASHBOARD_RANGE_TYPES_DICTIONARY,
  DATE_RANGE_DEFAULT_HISTOGRAM_KEY_TYPES,
  QUERY_STATUS,
} from "@utils/constants/dashboard/dashboardModuleConstants";

import { RADAR_CHART_INITIAL_STATE, RADAR_CHART_INITIAL_STATE_TYPE, SERIES_TYPE } from "./helpers";
import { LawsuitResponsible } from "@customTypes/user/responsible";
import { CaseType } from "@customTypes/components/filters";
import { LawsuitUpdatesRadarChartResponse } from "@customTypes/services/responses";
import { ONE_HOUR_MILLISECONDS } from "@utils/constants/react-query";

const parseLawsuitUpdatesRadarChartResponse = (
  response: LawsuitUpdatesRadarChartResponse = [],
  allResponsible: LawsuitResponsible[],
  selectedResponsibleIds: string[]
) => {
  const series: RADAR_CHART_INITIAL_STATE_TYPE["series"] = {
    withUpdates: [
      {
        name: "Com movimentação",
        data: [],
      },
    ],
    withoutUpdates: [
      {
        name: "Sem movimentação",
        data: [],
      },
    ],
  };

  const users = [
    ...new Set<string>(
      selectedResponsibleIds.map(
        (responsibleId) =>
          allResponsible.find((responsible) => responsible.id === responsibleId)?.nickname
      )
    ),
  ];

  selectedResponsibleIds.forEach((responsibleId) => {
    const bucketsRelatedToResponsible = response.filter(
      (bucket) => +bucket.secondKey === +responsibleId
    );
    const countByResponsible = bucketsRelatedToResponsible.reduce(
      (prevObj, bucket) =>
        bucket.firstKey === "in"
          ? {
              ...prevObj,
              withUpdates: prevObj.withUpdates + bucket.count,
            }
          : {
              ...prevObj,
              withoutUpdates: prevObj.withoutUpdates + bucket.count,
            },
      {
        withUpdates: 0,
        withoutUpdates: 0,
      }
    );
    series.withUpdates[0].data.push(countByResponsible.withUpdates);
    series.withoutUpdates[0].data.push(countByResponsible.withoutUpdates);
  });

  const isDataSeriesEmpty = (checkedSeries: SERIES_TYPE[]) => {
    const [{ data }] = checkedSeries;
    return data.length === 0 || data.every((category) => category === 0);
  };

  const noData = {
    notEnoughUsers: allResponsible.length < 3 || selectedResponsibleIds.length < 3,
    withUpdates: isDataSeriesEmpty(series.withUpdates),
    withoutUpdates: isDataSeriesEmpty(series.withoutUpdates),
  };

  return {
    users,
    series,
    noData,
  };
};

const useLawsuitUpdatesRadarChartQuery = (
  types: CaseType[],
  responsibleIds: string[],
  range: DATE_RANGE_DEFAULT_HISTOGRAM_KEY_TYPES
): [string, typeof RADAR_CHART_INITIAL_STATE, number, typeof refetch] => {
  const [updatesRadar, setUpdatesRadar] = useState(RADAR_CHART_INITIAL_STATE);
  const [status, setStatus] = useState(QUERY_STATUS.loading);

  const THIRTY_DAYS_AGO = moment().subtract(30, "days").format("YYYY-MM-DD");
  const YESTERDAY = moment().subtract(1, "days").format("YYYY-MM-DD");

  const FROM_DATE =
    (
      DASHBOARD_RANGE_TYPES_DICTIONARY as Record<
        string,
        { label: string; range: { fromDate: string; toDate: string } }
      >
    )[range]?.range?.fromDate ?? THIRTY_DAYS_AGO;

  const allResponsible = useSelector(getAllResponsiblesState);
  const PERSES_CODED_URLS = useSelector(getElasticSearchURLS);
  const perses = new PersesSearchAPI(PERSES_CODED_URLS);

  const { data, isSuccess, isFetching, isLoading, refetch, dataUpdatedAt } = useQuery(
    ["lawsuitUpdatesRadar", { types, responsibleIds, from: FROM_DATE, to: YESTERDAY }],
    /* istanbul ignore next */
    () =>
      perses
        .getLawsuitUpdatesRadarData(types, responsibleIds, FROM_DATE, YESTERDAY)
        .then(({ buckets }) => buckets),
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      staleTime: ONE_HOUR_MILLISECONDS,
    }
  );

  useEffect(() => {
    if (isLoading) return;
    if (isFetching) {
      setStatus(QUERY_STATUS.refeching);
      return;
    }
    if (isSuccess) {
      const parsedData = parseLawsuitUpdatesRadarChartResponse(
        data,
        allResponsible,
        responsibleIds
      );
      setUpdatesRadar(parsedData);
      setStatus(QUERY_STATUS.success);
    }
  }, [data, isSuccess, allResponsible, responsibleIds, isFetching, isLoading]);

  return [status, updatesRadar, dataUpdatedAt, refetch];
};

export default useLawsuitUpdatesRadarChartQuery;
